/**
 * Shared animated call-flow diagrams for Agents marketing pages.
 * Pure CSS/SVG — no external images required.
 */

function Chrome({ title, badge }) {
  return (
    <div className="flex h-10 items-center gap-2 border-b border-border bg-muted/60 px-3">
      <span className="h-2 w-2 rounded-full bg-rose-400" />
      <span className="h-2 w-2 rounded-full bg-amber-400" />
      <span className="h-2 w-2 rounded-full bg-emerald-400" />
      <span className="ml-2 truncate font-mono text-[10px] text-muted-foreground sm:text-[11px]">{title}</span>
      {badge && (
        <span className="ml-auto shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
          {badge}
        </span>
      )}
    </div>
  );
}

function Step({ n, label, sub, tone = "primary", delay = 0 }) {
  const tones = {
    primary: "border-primary/50 bg-primary/10 text-primary",
    muted: "border-border bg-background text-foreground",
    success: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    warn: "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200",
    human: "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  };
  return (
    <div
      className={`agent-step relative flex min-w-0 flex-1 flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-center ${tones[tone]}`}
      style={{ animationDelay: `${delay}s` }}
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background/80 text-[11px] font-bold shadow-sm">
        {n}
      </span>
      <p className="disp text-xs font-semibold leading-tight sm:text-sm">{label}</p>
      {sub && <p className="text-[10px] leading-snug text-muted-foreground sm:text-[11px]">{sub}</p>}
    </div>
  );
}

function Connector() {
  return (
    <div className="flex w-4 shrink-0 items-center justify-center sm:w-6" aria-hidden>
      <svg className="h-3 w-full text-primary/50" viewBox="0 0 24 12" fill="none">
        <path className="agent-dash" d="M0 6h18" stroke="currentColor" strokeWidth="2" />
        <path d="M16 2l6 4-6 4" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    </div>
  );
}

export function VoiceCallFlow({ className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-border bg-card shadow-lg ${className}`}
      data-testid="voice-call-flow"
    >
      <Chrome title="campaign · voice-outbound" badge="Voice" />
      <div className="space-y-4 p-4 sm:p-5">
        <div className="flex items-stretch gap-0.5 sm:gap-1">
          <Step n={1} label="Dial" sub="VICIdial / SIP" tone="muted" delay={0} />
          <Connector />
          <Step n={2} label="Play" sub="TTS / audio" tone="primary" delay={0.08} />
          <Connector />
          <Step n={3} label="Capture" sub="DTMF / speech" tone="warn" delay={0.16} />
          <Connector />
          <Step n={4} label="Branch" sub="Menu path" tone="success" delay={0.24} />
        </div>
        <div className="relative overflow-hidden rounded-xl border border-dashed border-border bg-muted/30 px-4 py-3">
          <div className="agent-wave absolute inset-x-0 bottom-0 h-8 opacity-40" aria-hidden />
          <p className="relative text-center text-xs text-muted-foreground">
            Scripted path — every caller hears the same prompts and presses the same keys.
          </p>
        </div>
      </div>
    </div>
  );
}

export function AICallFlow({ className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-border bg-card shadow-lg ${className}`}
      data-testid="ai-call-flow"
    >
      <Chrome title="campaign · ai-live-qualify" badge="Live AI" />
      <div className="space-y-4 p-4 sm:p-5">
        <div className="flex items-stretch gap-0.5 sm:gap-1">
          <Step n={1} label="Connect" sub="Answer / AMD" tone="muted" delay={0} />
          <Connector />
          <Step n={2} label="Live AI" sub="Greet + listen" tone="primary" delay={0.08} />
          <Connector />
          <Step n={3} label="Qualify" sub="Talk · reason" tone="warn" delay={0.16} />
          <Connector />
          <Step n={4} label="Outcome" sub="Book / transfer" tone="success" delay={0.24} />
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {["Caller speaks freely", "AI follows script + KB", "Human only if needed"].map((t, i) => (
            <div
              key={t}
              className="agent-step rounded-lg border border-border bg-background px-3 py-2 text-center text-[11px] text-muted-foreground"
              style={{ animationDelay: `${0.3 + i * 0.08}s` }}
            >
              {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HybridCallFlow({ className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-border bg-card shadow-lg ${className}`}
      data-testid="hybrid-call-flow"
    >
      <Chrome title="campaign · hybrid-cost-save" badge="Hybrid" />
      <div className="space-y-4 p-4 sm:p-5">
        <div className="flex items-stretch gap-0.5 sm:gap-1">
          <Step n={1} label="Voice" sub="Cheap open" tone="muted" delay={0} />
          <Connector />
          <Step n={2} label="Filter" sub="DTMF / intent" tone="warn" delay={0.08} />
          <Connector />
          <Step n={3} label="AI" sub="Hot leads only" tone="primary" delay={0.16} />
          <Connector />
          <Step n={4} label="Human" sub="Warm transfer" tone="human" delay={0.24} />
        </div>
        <div className="relative h-16 overflow-hidden rounded-xl border border-border bg-muted/40">
          <div className="absolute inset-y-2 left-2 right-2 flex items-center gap-1">
            <div className="agent-bar h-8 flex-[3] rounded-md bg-muted-foreground/25" style={{ animationDelay: "0s" }} />
            <div className="agent-bar h-8 flex-[1.2] rounded-md bg-amber-500/40" style={{ animationDelay: "0.1s" }} />
            <div className="agent-bar h-8 flex-[2] rounded-md bg-primary/50" style={{ animationDelay: "0.2s" }} />
            <div className="agent-bar h-8 flex-[1] rounded-md bg-sky-500/45" style={{ animationDelay: "0.3s" }} />
          </div>
          <p className="absolute bottom-1 left-0 right-0 text-center text-[10px] text-muted-foreground">
            Spend rises only as the lead gets warmer
          </p>
        </div>
      </div>
    </div>
  );
}

export function AgentHeroVisual({ variant }) {
  if (variant === "voice") {
    return (
      <div
        className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-border bg-card shadow-xl"
        aria-hidden
        data-testid="agent-hero-voice"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_0%,hsl(var(--primary)/0.16),transparent_50%)]" />
        {/* Window chrome */}
        <div className="relative flex h-10 items-center gap-2 border-b border-border bg-muted/70 px-3">
          <span className="h-2 w-2 rounded-full bg-rose-400" />
          <span className="h-2 w-2 rounded-full bg-amber-400" />
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          <span className="ml-2 truncate font-mono text-[10px] text-muted-foreground sm:text-[11px]">
            flow · voice-outbound-remind
          </span>
          <span className="ml-auto rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
            Live dial
          </span>
        </div>

        <div
          className="relative grid h-[calc(100%-2.5rem)] grid-cols-[1fr_0.95fr] gap-0"
          style={{
            backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "14px 14px",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-background/80 via-background/40 to-transparent" />

          {/* Left: animated campaign nodes */}
          <div className="relative flex flex-col items-center justify-center gap-0 p-3 sm:p-4">
            <div className="voice-hero-node rounded-xl bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground shadow-md shadow-primary/25 sm:text-xs">
              Start
            </div>
            <div className="voice-hero-line h-4 w-px bg-primary/45" />
            <div className="voice-hero-node w-[92%] max-w-[11rem] rounded-2xl border-2 border-primary/60 bg-background p-2.5 shadow-md sm:p-3">
              <div className="flex items-center gap-2">
                <span className="grid h-7 w-7 place-items-center rounded-lg bg-primary/15 text-primary">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                </span>
                <div className="min-w-0">
                  <p className="disp truncate text-[11px] font-semibold sm:text-xs">Play audio</p>
                  <p className="truncate text-[9px] text-muted-foreground">TTS · reminder.wav</p>
                </div>
              </div>
              <div className="mt-2 flex h-5 items-end gap-0.5 rounded-md bg-muted/60 px-1.5 py-1">
                {[30, 55, 40, 80, 50, 95, 45, 70, 35, 60, 85, 40].map((h, i) => (
                  <span
                    key={i}
                    className="agent-eq w-1 flex-1 rounded-full bg-primary/75"
                    style={{ height: `${h}%`, animationDelay: `${i * 0.06}s` }}
                  />
                ))}
              </div>
            </div>
            <div className="voice-hero-line h-4 w-px bg-primary/40" />
            <div className="voice-hero-node w-[92%] max-w-[11rem] rounded-2xl border border-amber-500/45 bg-amber-500/10 p-2.5 shadow-sm sm:p-3" style={{ animationDelay: "0.12s" }}>
              <p className="disp text-[11px] font-semibold text-amber-800 dark:text-amber-200 sm:text-xs">Collect DTMF</p>
              <p className="text-[9px] text-muted-foreground">Press 1 confirm · 2 reschedule</p>
              <div className="mt-2 grid grid-cols-3 gap-1">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k, i) => (
                  <span
                    key={k}
                    className={`voice-hero-key grid place-items-center rounded-md border border-border/70 bg-background py-0.5 font-mono text-[10px] font-semibold ${
                      k === "1" ? "voice-hero-key--hot" : ""
                    }`}
                    style={{ animationDelay: `${0.4 + i * 0.05}s` }}
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>
            <svg className="voice-hero-node h-8 w-full max-w-[11rem] text-primary/45" viewBox="0 0 160 32" fill="none" style={{ animationDelay: "0.2s" }}>
              <path className="agent-dash" d="M80 0v10M80 10H40M80 10h40M40 10v18M120 10v18" stroke="currentColor" strokeWidth="2" />
              <circle cx="40" cy="28" r="2.5" fill="currentColor" />
              <circle cx="120" cy="28" r="2.5" fill="currentColor" />
            </svg>
            <div className="voice-hero-node grid w-[92%] max-w-[11rem] grid-cols-2 gap-1.5" style={{ animationDelay: "0.28s" }}>
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-2 py-2 text-center">
                <p className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">Confirm</p>
                <p className="text-[8px] text-muted-foreground">→ hangup</p>
              </div>
              <div className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-2 py-2 text-center">
                <p className="text-[10px] font-semibold text-sky-700 dark:text-sky-300">Reschedule</p>
                <p className="text-[8px] text-muted-foreground">→ transfer</p>
              </div>
            </div>
          </div>

          {/* Right: live call panel */}
          <div className="relative flex flex-col justify-between border-l border-border bg-background/85 p-3 backdrop-blur-sm sm:p-4">
            <div>
              <div className="flex items-center justify-between gap-2">
                <p className="disp text-xs font-semibold sm:text-sm">On the call</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[9px] font-semibold text-rose-600 dark:text-rose-300">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-500" /> REC
                </span>
              </div>
              <p className="mt-0.5 font-mono text-[9px] text-muted-foreground">+1 ··· ··· 4821 · 00:18</p>
            </div>

            <div className="my-2 space-y-2">
              <div className="voice-hero-bubble rounded-2xl rounded-tl-md border border-border bg-muted/50 px-2.5 py-2 text-[10px] leading-snug text-foreground sm:text-[11px]">
                “Hi, this is a reminder for your Tuesday 2pm appointment. Press <strong>1</strong> to confirm, or <strong>2</strong> to reschedule.”
              </div>
              <div className="voice-hero-bubble flex items-center gap-2 rounded-2xl rounded-tr-md bg-primary px-2.5 py-2 text-[10px] text-primary-foreground sm:text-[11px]" style={{ animationDelay: "0.45s" }}>
                <span className="voice-hero-key--hot grid h-6 w-6 place-items-center rounded-md bg-primary-foreground/20 font-mono text-xs font-bold">1</span>
                Caller pressed 1 — confirm
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-2 shadow-sm">
              <div className="flex items-center justify-between text-[9px] text-muted-foreground">
                <span>Audio path</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">TTS only · no Live LLM</span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                <div className="voice-hero-progress h-full rounded-full bg-primary" />
              </div>
              <div className="mt-2 flex justify-between gap-1 text-[8px] font-medium uppercase tracking-wide text-muted-foreground">
                <span>Play</span>
                <span>Capture</span>
                <span>Branch</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (variant === "ai") {
    return (
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-border bg-card shadow-xl" aria-hidden data-testid="agent-hero-ai">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,hsl(var(--primary)/0.2),transparent_50%)]" />
        <div className="relative flex h-full flex-col justify-between p-5 sm:p-6">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-500" />
            <span className="font-mono text-[11px] text-muted-foreground">Live session · duplex</span>
          </div>
          <div className="space-y-3">
            <div className="agent-bubble ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-sm text-primary-foreground shadow-md">
              Hi — this is Alex from Northline. Do you have a minute?
            </div>
            <div className="agent-bubble max-w-[80%] rounded-2xl rounded-bl-md border border-border bg-background px-4 py-2.5 text-sm shadow-sm" style={{ animationDelay: "0.35s" }}>
              Sure, what&apos;s this about?
            </div>
            <div className="agent-bubble ml-auto max-w-[88%] rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-sm text-primary-foreground shadow-md" style={{ animationDelay: "0.7s" }}>
              Great — I can walk you through options and book a slot.
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-300">Listening</span>
            <span>Pause when customer talks · KB grounded</span>
          </div>
        </div>
      </div>
    );
  }
  // hybrid
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-border bg-card shadow-xl" aria-hidden data-testid="agent-hero-hybrid">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--primary)/0.12),transparent_40%,hsl(199_80%_50%/0.1))]" />
      <div className="relative grid h-full grid-rows-3 gap-2 p-4 sm:p-5">
        {[
          { label: "Voice layer", sub: "Greeting · IVR · FAQ", w: "92%", tone: "bg-muted-foreground/20" },
          { label: "AI layer", sub: "Qualify · sell · book", w: "68%", tone: "bg-primary/45" },
          { label: "Human layer", sub: "Warm SIP transfer", w: "38%", tone: "bg-sky-500/40" },
        ].map((row, i) => (
          <div key={row.label} className="flex flex-col justify-center gap-1.5 rounded-xl border border-border/80 bg-background/60 px-3 py-2 backdrop-blur-sm">
            <div className="flex items-baseline justify-between gap-2">
              <p className="disp text-sm font-semibold">{row.label}</p>
              <p className="text-[10px] text-muted-foreground">{row.sub}</p>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div
                className={`agent-bar h-full rounded-full ${row.tone}`}
                style={{ width: row.w, animationDelay: `${i * 0.15}s` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const AGENT_PAGE_STYLES = `
  @keyframes agent-step-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes agent-dash { to { stroke-dashoffset: -20; } }
  @keyframes agent-eq {
    0%,100% { transform: scaleY(0.45); }
    50% { transform: scaleY(1); }
  }
  @keyframes agent-pulse-soft {
    0%,100% { transform: scale(1); box-shadow: 0 12px 40px hsl(var(--primary) / 0.25); }
    50% { transform: scale(1.04); box-shadow: 0 16px 48px hsl(var(--primary) / 0.35); }
  }
  @keyframes agent-bubble-in {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes agent-bar-grow {
    from { transform: scaleX(0.35); opacity: 0.5; }
    to { transform: scaleX(1); opacity: 1; }
  }
  @keyframes agent-wave-x {
    from { transform: translateX(0); }
    to { transform: translateX(-40%); }
  }
  .agent-step { animation: agent-step-in 0.55s ease both; }
  .agent-dash { stroke-dasharray: 4 4; animation: agent-dash 0.9s linear infinite; }
  .agent-eq { transform-origin: bottom; animation: agent-eq 1.1s ease-in-out infinite; }
  .agent-pulse { animation: agent-pulse-soft 3.2s ease-in-out infinite; }
  .agent-bubble { animation: agent-bubble-in 0.6s ease both; }
  .agent-bar { transform-origin: left center; animation: agent-bar-grow 0.9s ease both; }
  .agent-wave {
    background: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 8px,
      hsl(var(--primary) / 0.35) 8px,
      hsl(var(--primary) / 0.35) 10px
    );
    animation: agent-wave-x 2.4s linear infinite;
  }
  @keyframes voice-hero-node {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes voice-hero-key-hot {
    0%, 100% { background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); transform: scale(1); }
    40% { background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); transform: scale(1.08); }
    70% { background: hsl(var(--background)); color: hsl(var(--foreground)); transform: scale(1); }
  }
  @keyframes voice-hero-progress {
    from { width: 12%; }
    to { width: 100%; }
  }
  .voice-hero-node { animation: voice-hero-node 0.55s ease both; }
  .voice-hero-line { opacity: 0.85; }
  .voice-hero-key--hot { animation: voice-hero-key-hot 2.4s ease-in-out infinite; }
  .voice-hero-bubble { animation: agent-bubble-in 0.65s ease both; }
  .voice-hero-progress { animation: voice-hero-progress 3.6s ease-in-out infinite alternate; }
`;
